import React, { useRef, useState } from "react";
import { CellContext, createColumnHelper } from "@tanstack/react-table";
import { IDesviacion, ISearchByHistoricoDesviacion } from "../../entities/IDesviacion";
import { ITipo_Campo } from "../../helpers/IFormHelper";
import { shortDate } from "../../helpers/dateHelper";
import { usePromiseEffect } from "../../helpers/promiseEffect";
import { cognitoUserInfo, cognitoUserToken } from "../../helpers/userHelper";
import ICurrentUser from "../../entities/helper/ICurrentUser";
import DesviacionService from "../../services/DesviacionService";
import { ROW_COUNT, ROW_COUNT_MAX } from "../../helpers/variableHelper";
import { AdminListaPagination, IPagination } from "../../controls/AdminListaPagination";
import Loading from "../../cross/Loading";
import RenderTitle from "../../controls/RenderTitle";
import RenderValue from "../../controls/RenderValue";

interface IHistoricoDesviacion {
  items?: Array<IDesviacion>;
}

export default function HistoricoDesviacion({ items }: IHistoricoDesviacion) {
  const [token, setToken] = useState("");
  const totalPageList = useRef<number>(0);
  const [arrFiltroColumnas, setArrFiltroColumnas] = useState<Array<string>>([
    "CORRELATIVO",
    "VICEPRESIDENCIA",
    "GERENCIA_SUBGERENCIA",
    "UNIDAD_PROYECTO",
    "CONTRATISTA_SUBCONTRATISTA",
    "CONTRATO",
    "ESTADO",
    "POTENCIALIDAD",
    "DETECTADO_POR",
    "AUTOR",
    "FECHA_CREACION",
    "FECHA_MODIFICACION",
    "ADECUADAMENTE_EVALUADO",
    "ADECUADAMENTE_IDENTIFICADO_CONTROL_MATRIZ",
    "IMPACTO_RIESGO_IDENTIFICADO_MATRIZ",
  ]);
  const [listDesviaciones, setListDesviaciones] = useState<Array<IDesviacion>>(items || []);
  const [showFilters, setShowFilters] = useState(true);
  const [currentUser, setCurrentUser] = useState<ICurrentUser>();
  const [filtrosColumnas] = useState<ISearchByHistoricoDesviacion>({
    ORDENADOPOR: "ID",
    ORIENTACION: "DESC",
    PAGINA: 1,
    PAGINADO: ROW_COUNT,
  });
  const [isDownloading, setIsDownloading] = useState(false);
  console.log(currentUser);
  const component = (titulo: string) => {
    return <RenderTitle title={titulo} />;
  };

  const componentValue = (value: any) => {
    return <RenderValue value={value} />;
  };

  const componentAcciones = (value: any, value2: any) => {
    return (
      <div>
        <i
          data-testid={`testacc${value}`}
          hidden={arrFiltroColumnas.length === 0}
          className="bi bi-search iconSearch"
          title="Ver Desviación"
          onClick={() => {
            window.location.href = `/agregardesviacion?id=${value2.ID}`;
          }}></i>
      </div>
    );
  };

  usePromiseEffect(async () => {
    let token = await cognitoUserToken();
    setToken(token);
    let currUser = await cognitoUserInfo();
    setCurrentUser(currUser);

    await reload();
  }, []);

  const reload = async (filtros?: any) => {
    let f = filtros === undefined ? filtrosColumnas : filtros;
    let token = await cognitoUserToken();
    let desviaciones = await DesviacionService.getListadoHistorico(token, f);

    setListDesviaciones(!desviaciones ? items : desviaciones.data);
    totalPageList.current = desviaciones.pages;
  };

  const toggleFilter = (filter: string, checked: boolean) => {
    let arr = arrFiltroColumnas;
    setArrFiltroColumnas(checked ? [...arr, filter] : arr.filter((f) => f !== filter));
  };

  const handleExportExcel = async (pagination?: IPagination) => {
    let defaultPagination = pagination || {
      PAGINA: 1,
      PAGINADO: ROW_COUNT_MAX,
      ORDENADOPOR: "ID",
      ORIENTACION: "DESC",
    };
    setIsDownloading(true);
    await DesviacionService.getExcelHistoricoDesviaciones(token, {
      ...defaultPagination,
      COLUMNAS_A_EXPORTAR: arrFiltroColumnas.map((value) => `${value}`).join(","),
    });
    setIsDownloading(false);
  };

  const columnHelperDesvGeneradas = createColumnHelper<IDesviacion>();
  let columnsDefDesviaciones = [
    columnHelperDesvGeneradas.accessor((row) => row.ID, {
      id: "ID",
      cell: (info: CellContext<IDesviacion, string>) => componentValue(info.getValue()),
      header: () => component("ID"),
      tipo: ITipo_Campo.text,
      hide: !arrFiltroColumnas.includes("ID"),
    } as any),
    columnHelperDesvGeneradas.accessor((row) => row.CORRELATIVO, {
      id: "CORRELATIVO",
      cell: (info: CellContext<IDesviacion, string>) => componentValue(info.getValue()),
      header: () => component("Correlativo"),
      tipo: ITipo_Campo.text,
      hide: !arrFiltroColumnas.includes("CORRELATIVO"),
    } as any),
    columnHelperDesvGeneradas.accessor((row) => row.VICEPRESIDENCIA, {
      id: "VICEPRESIDENCIA",
      cell: (info: CellContext<IDesviacion, string>) => componentValue(info.getValue()),
      header: () => component("Vicepresidencia"),
      tipo: ITipo_Campo.text,
      hide: !arrFiltroColumnas.includes("VICEPRESIDENCIA"),
    } as any),
    columnHelperDesvGeneradas.accessor((row) => row.GERENCIA_SUBGERENCIA, {
      id: "GERENCIA_SUBGERENCIA",
      cell: (info: CellContext<IDesviacion, string>) => componentValue(info.getValue()),
      header: () => component("Gerencia Subgerencia"),
      tipo: ITipo_Campo.text,
      hide: !arrFiltroColumnas.includes("GERENCIA_SUBGERENCIA"),
    } as any),
    columnHelperDesvGeneradas.accessor((row) => row.UNIDAD_PROYECTO, {
      id: "UNIDAD_PROYECTO",
      cell: (info: CellContext<IDesviacion, string>) => componentValue(info.getValue()),
      header: () => component("Unidad Proyecto"),
      tipo: ITipo_Campo.text,
      hide: !arrFiltroColumnas.includes("UNIDAD_PROYECTO"),
    } as any),
    columnHelperDesvGeneradas.accessor((row) => row.CONTRATISTA_SUBCONTRATISTA, {
      id: "CONTRATISTA_SUBCONTRATISTA",
      cell: (info: CellContext<IDesviacion, string>) => componentValue(info.getValue()),
      header: () => component("Contratista Subcontratista"),
      tipo: ITipo_Campo.text,
      hide: !arrFiltroColumnas.includes("CONTRATISTA_SUBCONTRATISTA"),
    } as any),
    columnHelperDesvGeneradas.accessor((row) => row.CONTRATO, {
      id: "CONTRATO",
      cell: (info: CellContext<IDesviacion, string>) => componentValue(info.getValue()),
      header: () => component("Contrato"),
      tipo: ITipo_Campo.text,
      hide: !arrFiltroColumnas.includes("CONTRATO"),
    } as any),
    columnHelperDesvGeneradas.accessor((row) => row.ESTADO, {
      id: "ESTADO",
      cell: (info: CellContext<IDesviacion, string>) => componentValue(info.getValue()),
      header: () => component("Estado"),
      tipo: ITipo_Campo.text,
      hide: !arrFiltroColumnas.includes("ESTADO"),
    } as any),
    columnHelperDesvGeneradas.accessor((row) => row.POTENCIALIDAD, {
      id: "POTENCIALIDAD",
      cell: (info: CellContext<IDesviacion, string>) => componentValue(info.getValue()),
      header: () => component("Gravedad Potencial"),
      tipo: ITipo_Campo.text,
      hide: !arrFiltroColumnas.includes("POTENCIALIDAD"),
    } as any),
    columnHelperDesvGeneradas.accessor((row) => (row.DETECTADO_POR_DISPLAY === null ? row.DETECTADO_POR : row.DETECTADO_POR_DISPLAY), {
      id: "DETECTADO_POR",
      cell: (info: CellContext<IDesviacion, string>) => componentValue(info.getValue()),
      header: () => component("Detectado Por"),
      tipo: ITipo_Campo.text,
      hide: !arrFiltroColumnas.includes("DETECTADO_POR"),
    } as any),
    columnHelperDesvGeneradas.accessor((row) => (row.AUTOR_DISPLAY === null ? row.AUTOR : row.AUTOR_DISPLAY), {
      id: "AUTOR",
      cell: (info: CellContext<IDesviacion, string>) => componentValue(info.getValue()),
      header: () => component("Creado Por"),
      tipo: ITipo_Campo.text,
      hide: !arrFiltroColumnas.includes("AUTOR"),
    } as any),
    columnHelperDesvGeneradas.accessor((row) => row.FECHA_CREACION, {
      id: "FECHA_CREACION",
      cell: (info: CellContext<IDesviacion, Date>) => componentValue(shortDate(info.getValue())),
      header: () => component("Fecha de Creación"),
      tipo: ITipo_Campo.date,
      hide: !arrFiltroColumnas.includes("FECHA_CREACION"),
    } as any),
    columnHelperDesvGeneradas.accessor((row) => row.FECHA_MODIFICACION, {
      id: "FECHA_MODIFICACION",
      cell: (info: CellContext<IDesviacion, Date>) => componentValue(shortDate(info.getValue())),
      header: () => component("Fecha de Modificación"),
      tipo: ITipo_Campo.date,
      hide: !arrFiltroColumnas.includes("FECHA_MODIFICACION"),
    } as any),
    columnHelperDesvGeneradas.accessor((row) => row.ADECUADAMENTE_EVALUADO, {
      id: "ADECUADAMENTE_EVALUADO",
      cell: (
        info: CellContext<IDesviacion, boolean> //
      ) => componentValue(info.getValue() ? "Sí" : "No"),
      header: () => component("Está Adecuadamente Evaluado"),
      tipo: ITipo_Campo.check,
      hide: !arrFiltroColumnas.includes("ADECUADAMENTE_EVALUADO"),
    } as any),
    columnHelperDesvGeneradas.accessor((row) => row.ADECUADAMENTE_IDENTIFICADO_CONTROL_MATRIZ, {
      id: "ADECUADAMENTE_IDENTIFICADO",
      cell: (
        info: CellContext<IDesviacion, boolean> //
      ) => componentValue(info.getValue() ? "Sí" : "No"),
      header: () => component("Adecuadamente Identificado"),
      tipo: ITipo_Campo.check,
      hide: !arrFiltroColumnas.includes("ADECUADAMENTE_IDENTIFICADO_CONTROL_MATRIZ"),
    } as any),
    columnHelperDesvGeneradas.accessor((row) => row.IMPACTO_RIESGO_IDENTIFICADO_MATRIZ, {
      id: "IMPACTO_RIESGO_IDENTIFICADO",
      cell: (
        info: CellContext<IDesviacion, boolean> //
      ) => componentValue(info.getValue() ? "Sí" : "No"),
      header: () => component("Impacto de Riesgo Identificado"),
      tipo: ITipo_Campo.check,
      hide: !arrFiltroColumnas.includes("IMPACTO_RIESGO_IDENTIFICADO_MATRIZ"),
    } as any),
    columnHelperDesvGeneradas.accessor((row) => row.ID, {
      id: "ACCIONES",
      cell: (info: CellContext<IDesviacion, string>) => componentAcciones(info.getValue(), info.row.original),
      header: () => component("Opciones"),
      tipo: ITipo_Campo.text,
      disableSorting: true,
    } as any),
  ] as any;
  return (
    <div>
      {isDownloading && <Loading key="loadingDownload" cssContainer="loadingFlotante" />}
      <div className="row">
        <div className="col-12">
          <div className="rounded bg-white p-2 shadow">
            <div className="container-fluid mb-4">
              <h1 className="titPrincipal colorTit">Filtros Históricos</h1>

              <div className="card bg-light border p-3 pt-2 mt-2">
                <div className="row ajusteInput">
                  <div className="col-12">
                    <h6>
                      <span className="textSeccion">Filtros</span>
                      <a
                        data-testid="test2"
                        className="btn btn-light btn-sm"
                        data-bs-toggle="collapse"
                        role="button"
                        aria-expanded="false"
                        aria-controls="collapseExample"
                        onClick={() => {
                          setShowFilters(!showFilters);
                        }}>
                        <i className={`bi ${showFilters ? "bi-dash-circle" : "bi-plus-circle"}`}></i>
                      </a>
                    </h6>
                  </div>
                  {showFilters && (
                    <div className="row collapse show" id="cajaFiltros">
                      <div className="col-sm-12 col-md-4 col-lg-4">
                        <div className="form-check">
                          <input
                            data-testid="test3"
                            className="form-check-input"
                            type="checkbox"
                            value="ID"
                            defaultChecked={arrFiltroColumnas.includes("ID")}
                            onChange={(e) => toggleFilter(e.target.value, e.target.checked)}
                          />
                          <span className="form-check-label">ID</span>
                        </div>
                        <div className="form-check">
                          <input
                            data-testid="test4"
                            className="form-check-input"
                            type="checkbox"
                            value="CORRELATIVO"
                            defaultChecked={arrFiltroColumnas.includes("CORRELATIVO")}
                            onChange={(e) => toggleFilter(e.target.value, e.target.checked)}
                          />
                          <span className="form-check-label">Correlativo</span>
                        </div>
                        <div className="form-check">
                          <input
                            data-testid="test5"
                            className="form-check-input"
                            type="checkbox"
                            value="VICEPRESIDENCIA"
                            defaultChecked={arrFiltroColumnas.includes("VICEPRESIDENCIA")}
                            onChange={(e) => toggleFilter(e.target.value, e.target.checked)}
                          />
                          <span className="form-check-label">Vicepresidencia</span>
                        </div>
                        <div className="form-check">
                          <input
                            data-testid="test6"
                            className="form-check-input"
                            type="checkbox"
                            value="GERENCIA_SUBGERENCIA"
                            defaultChecked={arrFiltroColumnas.includes("GERENCIA_SUBGERENCIA")}
                            onChange={(e) => toggleFilter(e.target.value, e.target.checked)}
                          />
                          <span className="form-check-label">Gerencia Subgerencia</span>
                        </div>
                        <div className="form-check">
                          <input
                            data-testid="test7"
                            className="form-check-input"
                            type="checkbox"
                            value="UNIDAD_PROYECTO"
                            defaultChecked={arrFiltroColumnas.includes("UNIDAD_PROYECTO")}
                            onChange={(e) => toggleFilter(e.target.value, e.target.checked)}
                          />
                          <span className="form-check-label">Unidad Proyecto</span>
                        </div>
                        <div className="form-check">
                          <input
                            data-testid="test8"
                            className="form-check-input"
                            type="checkbox"
                            value="CONTRATISTA_SUBCONTRATISTA"
                            defaultChecked={arrFiltroColumnas.includes("CONTRATISTA_SUBCONTRATISTA")}
                            onChange={(e) => toggleFilter(e.target.value, e.target.checked)}
                          />
                          <span className="form-check-label">Contratista Subcontratista</span>
                        </div>
                        <div className="form-check">
                          <input
                            data-testid="test9"
                            className="form-check-input"
                            type="checkbox"
                            value="CONTRATO"
                            defaultChecked={arrFiltroColumnas.includes("CONTRATO")}
                            onChange={(e) => toggleFilter(e.target.value, e.target.checked)}
                          />
                          <span className="form-check-label">Contrato</span>
                        </div>
                        <div className="form-check">
                          <input
                            data-testid="test10"
                            className="form-check-input"
                            type="checkbox"
                            value="ESTADO"
                            defaultChecked={arrFiltroColumnas.includes("ESTADO")}
                            onChange={(e) => toggleFilter(e.target.value, e.target.checked)}
                          />
                          <span className="form-check-label">Estado</span>
                        </div>
                        <div className="form-check">
                          <input
                            data-testid="test11"
                            className="form-check-input"
                            type="checkbox"
                            value="POTENCIALIDAD"
                            defaultChecked={arrFiltroColumnas.includes("POTENCIALIDAD")}
                            onChange={(e) => toggleFilter(e.target.value, e.target.checked)}
                          />
                          <span className="form-check-label">Gravedad Potencial</span>
                        </div>
                        <div className="form-check">
                          <input
                            data-testid="test12"
                            className="form-check-input"
                            type="checkbox"
                            value="DETECTADO_POR"
                            defaultChecked={arrFiltroColumnas.includes("DETECTADO_POR")}
                            onChange={(e) => toggleFilter(e.target.value, e.target.checked)}
                          />
                          <span className="form-check-label">Detectado por</span>
                        </div>
                      </div>
                      <div className="col-sm-12 col-md-4 col-lg-4">
                        <div className="form-check">
                          <input
                            data-testid="test13"
                            className="form-check-input"
                            type="checkbox"
                            value="AUTOR"
                            defaultChecked={arrFiltroColumnas.includes("AUTOR")}
                            onChange={(e) => toggleFilter(e.target.value, e.target.checked)}
                          />
                          <span className="form-check-label">Creado por</span>
                        </div>
                        <div className="form-check">
                          <input
                            data-testid="test14"
                            className="form-check-input"
                            type="checkbox"
                            value="FECHA_CREACION"
                            defaultChecked={arrFiltroColumnas.includes("FECHA_CREACION")}
                            onChange={(e) => toggleFilter(e.target.value, e.target.checked)}
                          />
                          <span className="form-check-label">Fecha de Creación</span>
                        </div>
                        <div className="form-check">
                          <input
                            data-testid="test15"
                            className="form-check-input"
                            type="checkbox"
                            value="FECHA_MODIFICACION"
                            defaultChecked={arrFiltroColumnas.includes("FECHA_MODIFICACION")}
                            onChange={(e) => toggleFilter(e.target.value, e.target.checked)}
                          />
                          <span className="form-check-label">Fecha de Modificación</span>
                        </div>
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            value="ADECUADAMENTE_EVALUADO"
                            defaultChecked={arrFiltroColumnas.includes("ADECUADAMENTE_EVALUADO")}
                            onChange={(e) => toggleFilter(e.target.value, e.target.checked)}
                          />
                          <span className="form-check-label">Esta Adecuadamente Evaluado</span>
                        </div>
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            value="ADECUADAMENTE_IDENTIFICADO_CONTROL_MATRIZ"
                            defaultChecked={arrFiltroColumnas.includes("ADECUADAMENTE_IDENTIFICADO_CONTROL_MATRIZ")}
                            onChange={(e) => toggleFilter(e.target.value, e.target.checked)}
                          />
                          <span className="form-check-label">Adecuadamente Identificado</span>
                        </div>
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            value="IMPACTO_RIESGO_IDENTIFICADO_MATRIZ"
                            defaultChecked={arrFiltroColumnas.includes("IMPACTO_RIESGO_IDENTIFICADO_MATRIZ")}
                            onChange={(e) => toggleFilter(e.target.value, e.target.checked)}
                          />
                          <span className="form-check-label">Impacto de Riesgo Identificado</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="" id="collapseGeneradas">
                <div className="card card-body border-0 p-0 mt-2">
                  <div className="row">
                    <div className="col-12">
                      <div className="container-fluid mb-4">
                        <h1 className="titPrincipal colorTit mt-2"></h1>

                        <div className="row">
                          <div className="col-12 p-0">
                            <h1 className="titPrincipal colorTit mt-2">Datos</h1>
                            {arrFiltroColumnas.length > 0 ? (
                              <AdminListaPagination
                                defaultSortBy="ID"
                                sortDirection="DESC"
                                totalPages={totalPageList}
                                resultPerPage={ROW_COUNT}
                                callback={(pagination: any) => {
                                  reload(pagination);
                                }}
                                data={listDesviaciones || []}
                                reload={(pagination: any) => {
                                  reload(pagination);
                                }}
                                columnsDef={columnsDefDesviaciones}
                                hideAdd={true}
                                hideExport={true}
                              />
                            ) : (
                              <>
                                <span>Seleccione una columna para poder ver los datos del reporte</span>
                              </>
                            )}
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-success me-2"
                              onClick={() => {
                                handleExportExcel();
                              }}>
                              <i className="bi bi-file-earmark-excel"></i> Exportar Excel
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
